import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import FeatureImg from "../images/about2.png";
import ContactModal from "./ContactModal";
import { Link } from "react-router-dom";


const servicesList = [
  { key: "s1", featured: true, slug: "software-solutions" },
  { key: "s2", slug: "ai-solutions" },
  { key: "s3", slug: "blockchain" },
  { key: "s4", slug: "audits-and-consulting" },
  { key: "s5", slug: "training" },
];

const container = {
  show: { transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function OurServices() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({});
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };


  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white"
    >
      {/* Subtle grid background */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(to_right,#bfdbfe_1px,transparent_1px),linear-gradient(to_bottom,#bfdbfe_1px,transparent_1px)]
        bg-[size:48px_48px] opacity-[0.67]
        [mask-image:radial-gradient(ellipse_at_center,black_45%,transparent_75%)]"
      />

      {/* Decorative blobs */}
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-gray-200/30 blur-3xl rounded-full" />
      <div className="absolute -bottom-32 -right-32 w-[32rem] h-[32rem] bg-gray-200/30 blur-3xl rounded-full" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t("services.main_title")}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
            {t("services.main_subtitle")}
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {servicesList.map((service) =>
            service.featured ? (
              <motion.div
                key={service.key}
                variants={item}
                className="lg:col-span-2"
              >
                <div className="relative h-full rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden shadow-2xl ring-1 ring-white/10">
                  {/* Image */}
                  <div className="absolute inset-y-0 right-0 w-1/2 hidden md:block">
                    <img
                      src={FeatureImg}
                      alt=""
                      className="h-full w-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-l from-transparent to-gray-900/90" />
                  </div>

                  <div className="relative p-8 sm:p-10 md:max-w-[55%]">
                    <h3 className="text-2xl font-semibold text-white mb-4">
                      {t(`services.${service.key}_title`)}
                    </h3>

                    <p className="text-gray-300 text-sm leading-relaxed">
                      {t(`services.${service.key}_desc`)}
                    </p>

                    <AnimatePresence>
                      {expanded[service.key] && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-gray-400 text-sm mt-4"
                        >
                          {t(`services.${service.key}_more`)}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <Link
                      to={`/${service.slug}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white hover:opacity-80 transition"
                    >
                      {t("common.learn_more")} →
                    </Link>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key={toggleExpanded[service.key]} variants={item}>
                <div className="group relative h-full rounded-3xl bg-white border border-gray-200 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  {/* Accent bar */}
                  <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-600 to-indigo-500 opacity-0 group-hover:opacity-100 transition" />

                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {t(`services.${service.key}_title`)}
                  </h3>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t(`services.${service.key}_desc`)}
                  </p>

                  <AnimatePresence>
                    {toggleExpanded[service.key] && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-sm text-gray-500 mt-3"
                      >
                        {t(`services.${service.key}_more`)}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <Link
                    to={`/${service.slug}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800 transition"
                  >
                    {t("common.learn_more")}
                    <span className="transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </Link>
                </div>
              </motion.div>
            )
          )}
        </motion.div>
      </div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </section>
  );
}
